import React, { FC } from "react";
import { Box } from "@chakra-ui/react";
import StudentTileConnecting from "@/js/components/StudentTile/components/inactiveScreenStates/StudentTileConnecting";
import StudentNoActivityDetected from "@/js/components/StudentTile/components/inactiveScreenStates/StudentNoActivityDetected";
import CannotDisplayActivity from "@/js/components/StudentTile/components/inactiveScreenStates/CannotDisplayActivity";
import StudentReconnecting from "@/js/components/StudentTile/components/inactiveScreenStates/StudentReconnecting";
import CannotDisplayScreen from "@/js/components/StudentTile/components/inactiveScreenStates/CannotDisplayScreen";
import { colours } from "@/theme/colours";
import clsx from "clsx";
import { envConfig } from "@/config";
import { useClassroomLayoutStore } from "@/stores/v2/classroomLayoutStore";

interface InactiveStudentWindow {
	connecting: boolean;
	screenshotRetryUsed: boolean;
	issueOngoingUsed: boolean;
	useScreenshotRetry: () => void;
	useIssueOngoing: () => void;
}

const InactiveStudentWindow: FC<InactiveStudentWindow> = (props: InactiveStudentWindow) => {
	const [workingOnIt, setWorkingOnIt] = React.useState(false);
	const tileSize = useClassroomLayoutStore((state) => state.tileSize);

	const useRetry = () => {
		props.useScreenshotRetry();
		// Change to the 'working on it' state, to display the reconnecting animation
		setWorkingOnIt(true);
		// Set a timeout to reset the workingOnIt state after a delay
		setTimeout(() => {
			setWorkingOnIt(false);
		}, envConfig.STUDENT_ACTIVITY_RECONNECT_TIMEOUT_MILLIS);
	};

	const useIssueOngoing = () => {
		props.useIssueOngoing();
	};

	const getComponent = () => {
		// While the student tile is in the 'connecting' state, show the connecting tile
		if (props.connecting) {
			return <StudentTileConnecting />;
		}

		// If the screenshot retry has been used, we display a short animation to indicate that something is happening
		if (workingOnIt) {
			return <StudentReconnecting />;
		}

		// If the student has not used the screenshot retry, and we have exceeded the 'connecting' time,
		// we show the StudentNoActivityDetected component
		if (!props.screenshotRetryUsed) {
			return <StudentNoActivityDetected useScreenshotRetry={useRetry} />;
		}

		// If the student has used the screenshot retry, we allow them to notify us if there is still an issue
		if (!props.issueOngoingUsed) {
			return <CannotDisplayScreen useIssueOngoing={useIssueOngoing} />;
		}

		// Finally, there is nothing left to be done and CannotDisplayActivity is shown
		return <CannotDisplayActivity />;
	};

	return (
		<Box
			data-testid="inactive-student-window"
			backgroundColor={props.connecting ? colours.accent.blue[100] : colours.accent.purple[100]}
			borderBottomRadius={"4px"}
			height={"100%"}
			padding={clsx({
				"8px 12px 8px 12px": tileSize === "lg" || tileSize === "md",
				"8px": tileSize === "sm",
			})}
		>
			{getComponent()}
		</Box>
	);
};

export default InactiveStudentWindow;
